import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useTranslation } from 'react-i18next';
import config from '../../config/config.json';
import axios from 'axios';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import styles from 'assets/jss/material-kit-react/views/profilePage.js';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  section: {
    marginBottom: '20px',
  },
  guildCard: {
    padding: '15px',
    borderRadius: '6px',
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  guildLogo: {
    width: '40px',
    height: '40px',
    borderRadius: '4px',
    marginRight: '15px',
  },
  centeredContent: {
    textAlign: 'center',
  },
  centeredHeader: {
    textAlign: 'center',
    margin: 0,
  },
  externalLink: {
    background: '#1976d2',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '15px',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      background: '#1565c0',
      color: 'white',
      textDecoration: 'none',
    },
  },
  publishedBadge: {
    background: '#4caf50',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
  },
  typeBadge: {
    background: '#ff9800',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    marginLeft: '8px',
  },
  classBadge: {
    background: '#9c27b0',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    marginLeft: '8px',
  },
}));

type GuideDetailPageParams = Record<'id', string>;

interface Guild {
  id: number;
  name: string;
  slug: string;
  logo_url?: string;
}

interface Guide {
  id: number;
  title: string;
  name: string;
  description: string;
  type: string;
  class: string;
  url?: string;
  published: boolean;
  created_at: string;
  updated_at: string;
  guild?: Guild;
}

export function GuideDetailPage() {
  const { id } = useParams<GuideDetailPageParams>();
  const { t } = useTranslation();
  const classes = useStyles();

  useDocumentTitle(t('core.guideDetails'));

  const {
    data: guide,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['guide', id],
    queryFn: async () => {
      const response = await axios.get(`${config.backend_base_url}api/public/guide/${id}`);
      return response.data as Guide;
    },
    enabled: !!id,
  });

  if (!id) {
    return <div>Invalid guide ID</div>;
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !guide) {
    return <div>Guide not found</div>;
  }

  return (
    <div className={classes.mainContainer}>
      <div className={classes.container}>
        <Card>
          <CardHeader color="primary">
            <h4 className={classes.centeredHeader}>{t('core.guideDetails')}</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={12}>
                <div className={classes.centeredContent}>
                  <div style={{ marginBottom: '20px' }}>
                    <h3>{guide.title || guide.name}</h3>
                    <div style={{ marginTop: '8px' }}>
                      {guide.published && (
                        <span className={classes.publishedBadge}>{t('core.published')}</span>
                      )}
                      {guide.type && <span className={classes.typeBadge}>{guide.type}</span>}
                      {guide.class && <span className={classes.classBadge}>{guide.class}</span>}
                    </div>
                  </div>

                  {guide.description && (
                    <div className={classes.section}>
                      <h5>{t('core.description')}</h5>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{guide.description}</p>
                    </div>
                  )}

                  {guide.url && (
                    <div className={classes.section} style={{ textAlign: 'center' }}>
                      <a
                        href={guide.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.externalLink}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <polyline
                            points="15,3 21,3 21,9"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <line
                            x1="10"
                            y1="14"
                            x2="21"
                            y2="3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {t('core.viewExternal')}
                      </a>
                    </div>
                  )}

                  {guide.guild && (
                    <div className={classes.section}>
                      <h5>{t('core.associatedGuild')}</h5>
                      <div className={classes.guildCard}>
                        {guide.guild.logo_url && (
                          <img
                            src={guide.guild.logo_url}
                            alt={guide.guild.name}
                            className={classes.guildLogo}
                          />
                        )}
                        <div>
                          <h6 style={{ margin: '0' }}>
                            <Link
                              to={`/guild/${guide.guild.id}`}
                              style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                              {guide.guild.name}
                            </Link>
                          </h6>
                          <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                            @{guide.guild.slug}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
                    <div>
                      {t('core.created')}: {new Date(guide.created_at).toLocaleDateString()}
                    </div>
                    <div>
                      {t('core.updated')}: {new Date(guide.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </div>
      <BubbleContainer pageKey="guide" helpText={t('help.guide')} />
    </div>
  );
}
